import {FC} from "react";
import {useTranslation} from "react-i18next";
import {
  Oem,
  OrganizationInformation
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {SummaryCard} from "../../../create-hierarchy/steps/common";
import {EntityDetailsColumn, EntityStatus} from "./EntityDetailsColumn";
import "../../../create-hierarchy/steps/common/BuilderStep.scss";
import "./CreateNewEntityReviewStep.scss";

export interface TestEntityReviewStepProps {
  transactionId: string;
  organizationInformation: OrganizationInformation;
  oems?: Oem[];
  tenantChoice: "new" | "existing";
  tenantName: string;
  tenantId?: string;
  status?: EntityStatus;
  entityId?: string;
}

/**
 * CreateNewEntityReviewStep provides entity preview functionality for the test entity stepper.
 * Shows entity details using EntityDetailsColumn (following the OrganizationDetailsColumn pattern)
 * and includes summary cards for the entity and tenant being created/linked.
 * Follows the same styling and layout patterns as ReviewPlanStep.
 */
export const CreateNewEntityReviewStep: FC<TestEntityReviewStepProps> = ({
  transactionId,
  organizationInformation,
  oems,
  tenantChoice,
  tenantName,
  tenantId,
  status = "idle",
  entityId
}) => {
  const {t} = useTranslation();

  const isResult = status === "created" || status === "failed";
  const entityBaseKey = isResult
    ? "create-new-entity.result-summary.created"
    : "create-new-entity.plan-summary.creating";
  const entityCount = status === "failed" ? 0 : 1;

  const tenantQualifier = isResult
    ? tenantChoice === "new" ? "tenant-created" : "tenant-assigned"
    : tenantChoice === "new" ? "new-tenant" : "existing-tenant";
  const tenantBaseKey = isResult
    ? "create-new-entity.result-summary"
    : "create-new-entity.plan-summary";
  const tenantCount = status === "failed" ? 0 : 1;

  return (
    <section
      data-testid={"test-entity-review-step"}
      className={"hierarchy-builder-step create-new-entity-review-step"}>
      {/* Summary Cards Section */}
      <section data-testid={"summary-section"} className={"summary-section"}>
        <SummaryCard
          baseKey={entityBaseKey}
          count={entityCount}
        />
        <SummaryCard
          baseKey={tenantBaseKey}
          qualifier={tenantQualifier}
          count={tenantCount}
          name={tenantName}
        />
      </section>

      {/* Entity Details Section */}
      <section
        data-testid={"entity-section"}
        className={"entity-section"}>
        <EntityDetailsColumn
          transactionId={transactionId}
          organizationInformation={organizationInformation}
          oems={oems}
          tenantChoice={tenantChoice}
          tenantName={tenantName}
          tenantId={tenantId}
          status={status}
          entityId={entityId}
        />
      </section>
    </section>
  );
};
